import { useMemo, useState } from "react";
import { Ayuda } from "./graficos.js";

/**
 * La pestaña de Rankings: todas las keywords, ordenadas por volumen.
 *
 * Por VOLUMEN y no por posición, que es la diferencia entre mirar dónde estás y
 * mirar dónde importa estar. Una keyword en el puesto 3 que no busca nadie va
 * debajo de una en el 61 que buscan cuarenta mil veces al mes — y esa segunda
 * es la que hay que ver primero al abrir.
 *
 * ── La regla de la flecha ────────────────────────────────────────────────
 *
 * Subir de posición es que el NÚMERO BAJA. La flecha va por lo que pasó, no por
 * lo que hizo el número: verde hacia arriba cuando se ha mejorado, aunque el
 * número haya bajado de 61 a 15. Pintarla por el número es la forma más rápida
 * de que una recuperación se lea como un desastre.
 */

export interface FilaDeRanking {
  keyword: string;
  volumen: number | null;
  posicion: number | null;
  posicionAntes: number | null;
  /** Con signo: positivo ha subido de puesto. */
  puestosGanados: number | null;
  urlEnSerp: string | null;
  partida: number | null;
  historico: { mes: string; posicion: number | null }[];
}

export interface DatosDeRanking {
  mes: string | null;
  anterior: string | null;
  titulares: {
    suben: number;
    bajan: number;
    nuevas: number;
    perdidas: number;
    contra: string;
  } | null;
  keywords: FilaDeRanking[];
}

/** El mes en cristiano: `2026-08` → `ago 2026`. */
function mesCorto(mes: string): string {
  const nombres = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  const [a, m] = mes.split("-").map(Number) as [number, number];
  return `${nombres[m - 1]} ${a}`;
}

/**
 * La mini-tendencia de una fila: siete puntos y nada más.
 *
 * Va con el eje INVERTIDO, como todo lo que mide posiciones: la línea sube
 * cuando la keyword mejora. Los meses sin medir se saltan, no se dibujan a
 * cero — un hueco pintado como cero es una caída al fondo que no ocurrió.
 */
function MiniTendencia({
  puntos,
}: {
  puntos: { mes: string; posicion: number | null }[];
}) {
  const con = puntos.filter((p) => p.posicion !== null) as {
    mes: string;
    posicion: number;
  }[];
  if (con.length < 2) return <span className="mini-nada">—</span>;

  const ancho = 58;
  const alto = 18;
  const max = Math.max(...con.map((p) => p.posicion));
  const min = Math.min(...con.map((p) => p.posicion));
  const rango = max - min || 1;
  const paso = ancho / (con.length - 1);
  const y = (v: number) => ((v - min) / rango) * (alto - 4) + 2;

  const d = con
    .map((p, i) => `${(i * paso).toFixed(1)},${y(p.posicion).toFixed(1)}`)
    .join(" ");
  const mejora = con.at(-1)!.posicion < con[0]!.posicion;

  return (
    <svg
      className={mejora ? "mini mejor" : "mini peor"}
      viewBox={`0 0 ${ancho} ${alto}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`De la posición ${con[0]!.posicion} a la ${con.at(-1)!.posicion}`}
    >
      <polyline points={d} />
    </svg>
  );
}

/**
 * La URL, dicha para leerla.
 *
 * Se quita el dominio —es el mismo en las sesenta filas y no distingue nada—
 * pero la raíz NO se deja como una barra suelta: <<\/>> repetido sesenta veces
 * parece la tabla rota. Se dice <<portada>>, que es lo que es.
 *
 * Y que muchas keywords apunten a la portada no es un defecto de esta columna:
 * es lo que hay que ver. Sesenta búsquedas distintas compitiendo por la misma
 * página significa que no hay página para ninguna.
 */
function rutaLegible(url: string): string {
  const ruta = url.replace(/^https?:\/\/(www\.)?[^/]+/, "");
  if (!ruta || ruta === "/") return "portada";
  return decodeURIComponent(ruta.replace(/\/$/, ""));
}

/** Una cifra de titular, con su explicación. */
function Titular({
  n,
  que,
  ayuda,
  tono,
}: {
  n: number;
  que: string;
  ayuda: string;
  tono: "mejor" | "peor" | "neutro";
}) {
  return (
    <div className={`titular ${tono}`}>
      <strong>{n}</strong>
      <span>
        {que}
        <Ayuda texto={ayuda} />
      </span>
    </div>
  );
}

export function Rankings({ datos }: { datos: DatosDeRanking }) {
  const [busca, setBusca] = useState("");
  const [abierta, setAbierta] = useState<string | null>(null);

  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return datos.keywords;
    return datos.keywords.filter(
      (k) =>
        k.keyword.toLowerCase().includes(t) ||
        (k.urlEnSerp ?? "").toLowerCase().includes(t),
    );
  }, [busca, datos.keywords]);

  if (!datos.mes) {
    return (
      <p className="grafico-vacio">
        Todavía no hay ninguna captura de posiciones para este cliente. Llegan
        con el informe mensual de SE Ranking.
      </p>
    );
  }

  return (
    <div className="rankings">
      {datos.titulares ? (
        <div className="titulares">
          <Titular
            n={datos.titulares.suben}
            que="suben"
            tono="mejor"
            ayuda={`Keywords que han mejorado de puesto desde la captura de ${mesCorto(datos.titulares.contra)}. Mejorar es que el número de posición baje.`}
          />
          <Titular
            n={datos.titulares.bajan}
            que="bajan"
            tono="peor"
            ayuda={`Keywords que han empeorado desde ${mesCorto(datos.titulares.contra)}. Una caída grande en una keyword de mucho volumen es lo primero que hay que mirar.`}
          />
          <Titular
            n={datos.titulares.nuevas}
            que="nuevas"
            tono="neutro"
            ayuda="Keywords que no estaban en la captura anterior. Entrar en el seguimiento no es una victoria por sí solo: mira su posición."
          />
          <Titular
            n={datos.titulares.perdidas}
            que="perdidas"
            tono="peor"
            ayuda="Keywords que antes posicionaban y ahora no aparecen en ningún puesto medido. Suele ser una página que se cayó o dejó de ser indexable."
          />
        </div>
      ) : (
        <p className="pista">
          Sólo hay una captura ({mesCorto(datos.mes)}). Con una no hay con qué
          comparar: los titulares salen a partir de la segunda.
        </p>
      )}

      <div className="barra-buscar">
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar una keyword o una URL…"
          aria-label="Buscar entre las keywords"
        />
        <span className="pista">
          {filtradas.length} de {datos.keywords.length} · captura de{" "}
          {mesCorto(datos.mes)}
        </span>
      </div>

      <div className="tabla-envoltura">
        <table className="tabla-rankings">
          {/* Los anchos mandan desde aquí: dejar que los decida el contenido
              estiraba la columna de keyword por culpa de la fila de títulos. */}
          <colgroup>
            <col className="c-kw" />
            <col className="c-vol" />
            <col className="c-pos" />
            <col className="c-mov" />
            <col className="c-ten" />
            <col className="c-url" />
          </colgroup>
          <thead>
            <tr>
              <th className="c-kw">
                Keyword
                <Ayuda texto="La búsqueda que hace la gente en Google. Pulsa una fila para ver su histórico de posiciones." />
              </th>
              <th className="num c-vol">
                Volumen
                <Ayuda texto="Búsquedas al mes, según SE Ranking. La tabla va ordenada por aquí: una keyword en el puesto 3 que no busca nadie importa menos que una en el 61 que buscan cuarenta mil veces." />
              </th>
              <th className="num c-pos">
                Posición
                <Ayuda texto="En qué puesto de Google sales. 1 es el primer resultado; un guion es que no apareces en los puestos medidos." />
              </th>
              <th className="num c-mov">
                Movimiento
                <Ayuda texto="Cuántos puestos se han ganado o perdido desde la captura anterior. Verde arriba es mejorar, aunque el número de posición baje." />
              </th>
              <th className="c-ten">
                Tendencia
                <Ayuda texto="Las últimas capturas de esa keyword. El eje va invertido: la línea sube cuando la posición mejora." />
              </th>
              <th className="c-url">
                URL
                <Ayuda texto="La página tuya que sale en Google por esa búsqueda. Si salen dos páginas distintas por búsquedas parecidas, están compitiendo entre ellas." />
              </th>
            </tr>
          </thead>
          <tbody>
            {filtradas.length === 0 && (
              <tr>
                <td colSpan={6} className="sin-nada">
                  Ninguna keyword contiene «{busca}».
                </td>
              </tr>
            )}
            {filtradas.map((k) => (
              <FilaKeyword
                key={k.keyword}
                fila={k}
                abierta={abierta === k.keyword}
                alPulsar={() =>
                  setAbierta(abierta === k.keyword ? null : k.keyword)
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilaKeyword({
  fila,
  abierta,
  alPulsar,
}: {
  fila: FilaDeRanking;
  abierta: boolean;
  alPulsar: () => void;
}) {
  const mov = fila.puestosGanados;
  return (
    <>
      {/*
       * La fila NO lleva `role="button"`, y eso no es un descuido.
       *
       * Se lo puse para poder abrirla con el teclado y rompió la tabla entera:
       * al darle a un `<tr>` un papel que no es de tabla, el navegador deja de
       * tratarlo como fila — le calcula `display: grid` y a sus celdas `block`.
       * El resultado era que la cabecera seguía siendo una tabla de verdad y el
       * cuerpo formaba su propia rejilla: los títulos en una columna y los
       * valores en otra.
       *
       * Costó encontrarlo porque no hay NINGUNA regla en la hoja de estilos que
       * ponga ese `display`. Lo ponía el papel ARIA, y buscarlo en el CSS era
       * buscar donde no estaba.
       *
       * Quien abre la fila es el botón de la keyword, que sí es un botón de
       * verdad. La fila conserva el clic por comodidad, sin prometer al lector
       * de pantalla que es algo que no es.
       */}
      <tr
        className={abierta ? "fila-kw abierta" : "fila-kw"}
        onClick={alPulsar}
      >
        <td className="kw c-kw">
          <button
            type="button"
            className="kw-txt"
            aria-expanded={abierta}
            onClick={(e) => {
              e.stopPropagation();
              alPulsar();
            }}
          >
            {fila.keyword}
          </button>
        </td>
        <td className="num c-vol">
          {fila.volumen === null ? "—" : fila.volumen.toLocaleString("es-ES")}
        </td>
        <td className="num pos c-pos">{fila.posicion ?? "—"}</td>
        <td className="num c-mov">
          {mov === null || mov === 0 ? (
            <span className="quieta">·</span>
          ) : (
            <span className={mov > 0 ? "sube" : "baja"}>
              {mov > 0 ? "▲" : "▼"} {Math.abs(mov)}
            </span>
          )}
        </td>
        <td className="c-ten">
          <MiniTendencia puntos={fila.historico} />
        </td>
        <td className="url c-url">
          {fila.urlEnSerp ? (
            <a
              href={fila.urlEnSerp}
              target="_blank"
              rel="noreferrer"
              title={fila.urlEnSerp}
              onClick={(e) => e.stopPropagation()}
            >
              {rutaLegible(fila.urlEnSerp)}
            </a>
          ) : (
            <span className="quieta">—</span>
          )}
        </td>
      </tr>
      {abierta && (
        <tr className="detalle-kw">
          <td colSpan={6}>
            <div className="historico">
              {fila.historico.map((h) => (
                <span
                  key={h.mes}
                  className={h.posicion === null ? "hito vacio" : "hito"}
                >
                  <b>{h.posicion ?? "—"}</b>
                  {mesCorto(h.mes)}
                </span>
              ))}
            </div>
            <p className="pista">
              {fila.partida !== null && (
                <>
                  Se partía del puesto <b>{fila.partida}</b>.{" "}
                </>
              )}
              {fila.urlEnSerp ? (
                <>
                  Posiciona con <span className="mono">{fila.urlEnSerp}</span>.
                </>
              ) : (
                "SE Ranking no dice qué página sale por esta búsqueda."
              )}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}
